import { useQuery } from '@tanstack/react-query';
import { useEffect, useState, useRef, useCallback } from 'react';
import { fetchSSEToken } from 'shared/api/services/sse';

const MAX_RETRY_ATTEMPTS = 5;
const INITIAL_RETRY_DELAY = 1000; // 1 second

export const useSSE = <T>(
  endpoint: string,
  onMessage: (data: T) => void,
  params?: string,
  isTailMode: boolean = false
) => {
  const [isConnected, setIsConnected] = useState(false);
  const eventStreamRef = useRef<EventSource | null>(null);
  const retryCountRef = useRef(0);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);
  const { data, refetch } = useQuery({
    queryKey: ['sse-token', endpoint],
    queryFn: fetchSSEToken,
    retry: 5,
    retryDelay: 15000,
    refetchOnReconnect: true,
    enabled: isTailMode,
  });

  const connectSSE = useCallback(async () => {
    if (!isTailMode || !isMountedRef.current) return;

    try {
      // If we don't have token data, try to refetch it
      if (!data) {
        await refetch();
        return;
      }

      if (!isMountedRef.current) return;

      // Close existing connection if any
      if (eventStreamRef.current) {
        eventStreamRef.current.close();
      }

      const eventStream = new EventSource(
        `${process.env.NOTIFICATIONS_API}/${endpoint}?session=${data?.sessionId}${params || ''}`
      );

      eventStreamRef.current = eventStream;

      eventStream.onopen = () => {
        if (isMountedRef.current) {
          setIsConnected(true);
          retryCountRef.current = 0; // Reset retry count on successful connection
        }
      };

      // Message received and JSON parsed
      eventStream.onmessage = (event) => {
        if (isMountedRef.current) {
          let parsedData;
          try {
            parsedData = JSON.parse(event.data);
          } catch {
            // If JSON parsing fails, use the raw data
            parsedData = event.data;
          }
          onMessage(parsedData);
        }
      };

      // Error handling with reconnection logic
      eventStream.onerror = async () => {
        if (!isMountedRef.current) return;

        setIsConnected(false);
        eventStream.close();

        // Attempt to reconnect if under max retry limit
        if (retryCountRef.current < MAX_RETRY_ATTEMPTS) {
          const delay = INITIAL_RETRY_DELAY * 2 ** retryCountRef.current; // Exponential backoff
          retryCountRef.current += 1;

          retryTimeoutRef.current = setTimeout(async () => {
            if (isMountedRef.current) {
              // Try to refetch token before reconnecting (in case token expired or network changed)
              await refetch();
              connectSSE();
            }
          }, delay);
        }
      };
    } catch (error) {
      // Failed to establish SSE connection
      if (isMountedRef.current) {
        setIsConnected(false);
      }
    }
  }, [endpoint, onMessage, params, isTailMode, data, refetch]);

  useEffect(() => {
    isMountedRef.current = true;
    connectSSE();

    // Cleanup on unmount
    return () => {
      isMountedRef.current = false;

      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }

      if (eventStreamRef.current) {
        eventStreamRef.current.close();
        setIsConnected(false);
      }
    };
  }, [connectSSE]);

  return { isConnected };
};
